import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, message, Spin } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { Button } from '../../design-system';
import { useClientAuth, useClientAuthDispatch } from '../../context/ClientAuthContext';
import clientAPI from '../../service/ApiServiceClientPortal';

const BRAND_YELLOW = '#FED230';
const BRAND_BLACK = '#1B1B1B';
const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

const styles = {
    wrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#fafafa',
    },
    card: {
        background: '#fff',
        borderRadius: 8,
        padding: '48px 40px',
        width: 420,
        maxWidth: '90vw',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    },
    logo: {
        textAlign: 'center',
        fontSize: 24,
        fontWeight: 700,
        color: BRAND_BLACK,
        letterSpacing: 2,
        marginBottom: 8,
    },
    subtitle: {
        textAlign: 'center',
        color: '#888',
        fontSize: 14,
        marginBottom: 32,
    },
    otpContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
        marginBottom: 24,
    },
    otpInput: {
        width: 48,
        height: 52,
        textAlign: 'center',
        fontSize: 22,
        fontWeight: 600,
        borderRadius: 6,
        border: `1px solid #d9d9d9`,
    },
    otpInputFocused: {
        borderColor: BRAND_YELLOW,
        boxShadow: `0 0 0 2px rgba(254, 210, 48, 0.2)`,
    },
    resendRow: {
        textAlign: 'center',
        marginBottom: 24,
        fontSize: 13,
        color: '#888',
    },
    resendLink: {
        color: BRAND_BLACK,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 500,
        background: 'none',
        border: 'none',
        padding: 0,
        fontSize: 13,
    },
    emailDisplay: {
        textAlign: 'center',
        marginBottom: 16,
        fontSize: 14,
    },
    backLink: {
        textAlign: 'center',
        marginTop: 16,
    },
    backButton: {
        color: BRAND_BLACK,
        textDecoration: 'underline',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        fontSize: 13,
        padding: 0,
    },
};

const ClientLogin = () => {
    const [step, setStep] = useState('email'); // 'email' | 'otp'
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const inputRefs = useRef([]);
    const timerRef = useRef(null);

    const navigate = useNavigate();
    const { jwtToken } = useClientAuth();
    const dispatch = useClientAuthDispatch();

    // If already authenticated, redirect
    useEffect(() => {
        if (jwtToken) {
            navigate('/client-section', { replace: true });
        }
    }, [jwtToken, navigate]);

    const startResendTimer = useCallback(() => {
        setResendTimer(RESEND_SECONDS);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    const handleRequestOtp = async () => {
        if (!email || !email.includes('@')) {
            message.error('Please enter a valid email address');
            return;
        }
        setLoading(true);
        try {
            const response = await clientAPI.post('/client-portal/auth/request-otp', { email });
            const msg = response.data?.data?.message || response.data?.message || 'Verification code sent to your email';
            message.success(msg);
            setStep('otp');
            setOtp(Array(OTP_LENGTH).fill(''));
            startResendTimer();
            // Focus first OTP input after render
            setTimeout(() => inputRefs.current[0]?.focus(), 100);
        } catch (err) {
            const errMsg = err.response?.data?.data?.message || err.response?.data?.message || 'Failed to send verification code';
            message.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (code) => {
        const otpCode = code || otp.join('');
        if (otpCode.length !== OTP_LENGTH) {
            message.error(`Please enter the ${OTP_LENGTH}-character code`);
            return;
        }
        setLoading(true);
        try {
            const response = await clientAPI.post('/client-portal/auth/verify-otp', { email, code: otpCode });
            const data = response.data?.data || response.data;
            dispatch({
                type: 'login',
                payload: {
                    contactId: data.contact?.id,
                    token: data.token,
                    contact: data.contact,
                },
            });
            message.success('Login successful');
            navigate('/client-section', { replace: true });
        } catch (err) {
            const errMsg = err.response?.data?.data?.message || err.response?.data?.message || 'Invalid or expired code';
            message.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendTimer > 0) return;
        setLoading(true);
        try {
            await clientAPI.post('/client-portal/auth/request-otp', { email });
            message.success('A new code has been sent');
            setOtp(Array(OTP_LENGTH).fill(''));
            startResendTimer();
            setTimeout(() => inputRefs.current[0]?.focus(), 100);
        } catch (err) {
            const errMsg = err.response?.data?.data?.message || err.response?.data?.message || 'Failed to resend code';
            message.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (index, value) => {
        // Only allow alphanumeric characters
        const char = value.replace(/[^a-zA-Z0-9]/g, '').slice(-1).toUpperCase();
        const newOtp = [...otp];
        newOtp[index] = char;
        setOtp(newOtp);

        if (char && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all fields filled
        if (char && index === OTP_LENGTH - 1) {
            const fullCode = newOtp.join('');
            if (fullCode.length === OTP_LENGTH) {
                handleVerifyOtp(fullCode);
            }
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, OTP_LENGTH);
        if (pasted.length > 0) {
            const newOtp = [...otp];
            for (let i = 0; i < pasted.length; i++) {
                newOtp[i] = pasted[i];
            }
            setOtp(newOtp);
            const nextIndex = Math.min(pasted.length, OTP_LENGTH - 1);
            inputRefs.current[nextIndex]?.focus();

            if (pasted.length === OTP_LENGTH) {
                handleVerifyOtp(pasted);
            }
        }
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.card}>
                <div style={styles.logo}>MEDIATROPY</div>
                <div style={styles.subtitle}>Client Portal</div>

                <Spin spinning={loading}>
                    {step === 'email' && (
                        <Form onFinish={handleRequestOtp} layout="vertical">
                            <Form.Item label="Email Address">
                                <Input
                                    prefix={<MailOutlined />}
                                    placeholder="Enter your email"
                                    size="large"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onPressEnter={handleRequestOtp}
                                    autoFocus
                                />
                            </Form.Item>
                            <Button
                                variant="primary"
                                onClick={handleRequestOtp}
                                style={{ width: '100%', height: 44, fontSize: 15 }}
                            >
                                Send Verification Code
                            </Button>
                        </Form>
                    )}

                    {step === 'otp' && (
                        <>
                            <div style={styles.emailDisplay}>
                                Code sent to <strong>{email}</strong>
                            </div>

                            <div style={styles.otpContainer} onPaste={handleOtpPaste}>
                                {otp.map((char, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        type="text"
                                        maxLength={1}
                                        value={char}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        style={styles.otpInput}
                                        autoComplete="off"
                                    />
                                ))}
                            </div>

                            <div style={styles.resendRow}>
                                {resendTimer > 0 ? (
                                    <span>Resend code in {resendTimer}s</span>
                                ) : (
                                    <button style={styles.resendLink} onClick={handleResend}>
                                        Resend code
                                    </button>
                                )}
                            </div>

                            <Button
                                variant="primary"
                                onClick={() => handleVerifyOtp()}
                                style={{ width: '100%', height: 44, fontSize: 15 }}
                            >
                                <LockOutlined style={{ marginRight: 6 }} />
                                Verify & Login
                            </Button>

                            <div style={styles.backLink}>
                                <button
                                    style={styles.backButton}
                                    onClick={() => {
                                        setStep('email');
                                        setOtp(Array(OTP_LENGTH).fill(''));
                                    }}
                                >
                                    Use a different email
                                </button>
                            </div>
                        </>
                    )}
                </Spin>
            </div>
        </div>
    );
};

export default ClientLogin;
